import { useEffect, useRef, useState } from 'react';
import { toast } from 'sonner';
import { Loader2 } from 'lucide-react';
import { Button } from './ui/button';
import ClientSignPlacer from './ClientSignPlacer';

// Pad de signature tactile
function SignPad({ onDone, docName }) {
  const canvasRef = useRef(null);
  const drawingRef = useRef(false);
  const drawnRef = useRef(false);
  const lastRef = useRef({ x: 0, y: 0 });
  const [hasDrawn, setHasDrawn] = useState(false);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    canvas.width = canvas.parentElement.offsetWidth;
    canvas.height = 190;
    const ctx = canvas.getContext('2d');
    ctx.fillStyle = 'white'; ctx.fillRect(0, 0, canvas.width, canvas.height);
    ctx.strokeStyle = '#1A1A1A'; ctx.lineWidth = 3; ctx.lineCap = 'round'; ctx.lineJoin = 'round';
  }, []);

  const pos = (e) => {
    const canvas = canvasRef.current;
    const rect = canvas.getBoundingClientRect();
    const sx = canvas.width / rect.width, sy = canvas.height / rect.height;
    const t = e.touches?.[0] || e.changedTouches?.[0];
    const cx = t ? t.clientX : e.clientX, cy = t ? t.clientY : e.clientY;
    return { x: (cx - rect.left) * sx, y: (cy - rect.top) * sy };
  };
  const start = (e) => { e.preventDefault(); const ctx = canvasRef.current.getContext('2d'); const p = pos(e); ctx.beginPath(); ctx.moveTo(p.x, p.y); lastRef.current = p; drawingRef.current = true; };
  const move = (e) => { if (!drawingRef.current) return; e.preventDefault(); const ctx = canvasRef.current.getContext('2d'); const p = pos(e); ctx.beginPath(); ctx.moveTo(lastRef.current.x, lastRef.current.y); ctx.lineTo(p.x, p.y); ctx.stroke(); lastRef.current = p; if (!drawnRef.current) { drawnRef.current = true; setHasDrawn(true); } };
  const stop = (e) => { if (e) e.preventDefault(); drawingRef.current = false; };
  const clear = () => { const canvas = canvasRef.current; const ctx = canvas.getContext('2d'); ctx.fillStyle = 'white'; ctx.fillRect(0, 0, canvas.width, canvas.height); ctx.strokeStyle = '#1A1A1A'; ctx.lineWidth = 3; ctx.lineCap = 'round'; ctx.lineJoin = 'round'; drawnRef.current = false; setHasDrawn(false); };
  const next = () => { if (!drawnRef.current) { toast.error('Veuillez signer avant de continuer'); return; } onDone(canvasRef.current.toDataURL('image/png')); };

  return (
    <div data-testid="sign-pad">
      <p className="text-sm font-medium text-[#2D2D2D] mb-2">1. Signez ci-dessous{docName ? <> : <span className="font-semibold">{docName}</span></> : null}</p>
      <div className="relative border-2 border-[#8B7355] rounded-xl overflow-hidden bg-white">
        <canvas ref={canvasRef} className="w-full touch-none" style={{ height: 190, touchAction: 'none' }}
          onMouseDown={start} onMouseMove={move} onMouseUp={stop} onMouseLeave={stop}
          onTouchStart={start} onTouchMove={move} onTouchEnd={stop} onTouchCancel={stop} />
        {!hasDrawn && <div className="absolute inset-0 flex items-center justify-center pointer-events-none"><p className="text-[#9CA3AF] text-lg">Signez ici ✍️</p></div>}
      </div>
      <div className="flex gap-2 mt-3">
        <Button variant="outline" onClick={clear} className="flex-1 border-2 border-[#8B7355]/60" data-testid="sign-clear">Effacer</Button>
        <Button onClick={next} className={`flex-1 ${hasDrawn ? 'bg-green-600 hover:bg-green-700' : 'bg-[#8B7355]'} text-white font-semibold`} data-testid="sign-next">Continuer →</Button>
      </div>
    </div>
  );
}

// Flux complet : dessiner -> placer/dupliquer -> valider.
// onComplete(signatureDataUrl, placements[]) ; placements = [{x,y,page,width_frac}]
export default function SignatureFlow({ pdfUrl, docName, onComplete, onCancel, busy }) {
  const [stage, setStage] = useState('draw');
  const [sig, setSig] = useState(null);
  const [placements, setPlacements] = useState({ signatures: [], texts: [] });

  if (stage === 'draw') {
    return (
      <div className="space-y-3" data-testid="signature-flow-draw">
        <SignPad docName={docName} onDone={(dataUrl) => { setSig(dataUrl); setStage('place'); }} />
        {onCancel && <Button variant="ghost" onClick={onCancel} className="w-full text-[#6B7280]" data-testid="sign-cancel">Annuler</Button>}
      </div>
    );
  }

  return (
    <div className="space-y-3" data-testid="signature-flow-place">
      <p className="text-sm font-medium text-[#2D2D2D]">2. Placez votre signature — glissez, redimensionnez, <span className="text-[#8B7355] font-semibold">dupliquez</span>, ou <span className="text-blue-600 font-semibold">ajoutez un texte</span> (date, franchise…)</p>
      <ClientSignPlacer pdfUrl={pdfUrl} signatureUrl={sig} onChange={setPlacements} />
      <div className="flex gap-2">
        <Button variant="outline" onClick={() => setStage('draw')} disabled={busy} className="flex-1 border-2 border-[#8B7355]/60" data-testid="sign-redo">← Refaire</Button>
        <Button onClick={() => onComplete(sig, placements)} disabled={busy} className="flex-1 bg-green-600 hover:bg-green-700 text-white font-semibold" data-testid="sign-validate">
          {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : '✓ Valider'}
        </Button>
      </div>
    </div>
  );
}
